/**
 * FILE: MechanicManagement.jsx
 */

import React, {
  useState,
  useEffect,
  useRef,
  useCallback,
  useContext,
} from "react";
import { createPortal } from "react-dom";
import {
  FiMoreVertical,
  FiEye,
  FiLock,
  FiUnlock,
  FiTrash2,
  FiUsers,
  FiAlertCircle,
  FiCheckCircle,
  FiX,
  FiMapPin,
  FiPhone,
  FiMail,
  FiCalendar,
  FiBriefcase,
  FiFileText,
  FiExternalLink,
  FiShield,
  FiTool,
  FiChevronLeft,
  FiChevronRight,
  FiStar,
} from "react-icons/fi";
import { FaRupeeSign } from "react-icons/fa";
import AdminMasterLayout from "../../../../layout/superadmin/MasterLayout";
import axios from "axios";
import Swal from "sweetalert2";
import AppContext from "../../../../context/AppContext";
import "../../../../assets/css/superadmin/usersmangment/mechanicmanagment/Mechanicmanagment.css";

const getInitials = (name) =>
  (name || "")
    .split(" ")
    .map((n) => n[0])
    .join("")
    .toUpperCase()
    .slice(0, 2);

const cap = (s) => (s ? s.charAt(0).toUpperCase() + s.slice(1) : "");

const MechanicManagement = () => {
  const { baseUrl, authToken, baseUrlForFile } = useContext(AppContext);
  const [errorMessage, setErrorMessage] = useState("");

  const [loading, setLoading] = useState(true);
  const [mechanics, setMechanics] = useState([]);
  const [activeTab, setActiveTab] = useState("all");
  const [currentPage, setCurrentPage] = useState(1);
  const [detailMech, setDetailMech] = useState(null);
  const [showPdf, setShowPdf] = useState(false);
  const [cnicTicked, setCnicTicked] = useState(false);
  const [specTicked, setSpecTicked] = useState(false);
  const [cnicDocLoading, setCnicDocLoading] = useState(false);
  const [specDocLoading, setSpecDocLoading] = useState(false);
  const [approveLoading, setApproveLoading] = useState(false);
  const [rejectLoading, setRejectLoading] = useState(false);
  const [openMenu, setOpenMenu] = useState(null);
  const [menuPos, setMenuPos] = useState({ top: 0, right: 0 });

  const ITEMS_PER_PAGE = 5;

  const viewAllMechanics = () => {
    setLoading(true);
    axios
      .get(`${baseUrl}/superadmin/mechanics/viewAllMechanics`, {
        headers: { Authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.success == true) {
          setMechanics(response.data.mechanics);
        } else if (response.data.hasOwnProperty("error")) {
          setErrorMessage(response.data.error);
          Swal.fire({
            icon: "error",
            title: "Error",
            text: response.data.error,
          });
        }
        setLoading(false);
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to load Mechanics.",
        });
        setLoading(false);
      });
  };

  const handleToggleStatus = (userId) => {
    const formData = new FormData();
    formData.append("id", userId);
    axios
      .post(`${baseUrl}/superadmin/mechanics/updateStatus`, formData, {
        headers: { authorization: `Bearer ${authToken}` },
      })
      .then((response) => {
        if (response.data.success == true) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "User status updated successfully!",
          });
          viewAllMechanics();
        }
      })
      .catch((error) => {
        console.log(error);
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to update mechanic status.",
        });
      });
  };

  const acceptRequest = () => {
    setApproveLoading(true);
    const formData = new FormData();
    formData.append("id", detailMech.id);
    formData.append("verification_status", "verified");
    axios
      .post(
        `${baseUrl}/superadmin/mechanics/updateVerificationStatus`,
        formData,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      )
      .then((response) => {
        if (response.data.success) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Mechanic verified successfully!",
          });
          setDetailMech(null);
          viewAllMechanics();
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to verify mechanic.",
          });
        }
        setApproveLoading(false);
      })
      .catch(() => {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to verify mechanic.",
        });
        setApproveLoading(false);
      });
  };

  const rejectRequest = () => {
    setRejectLoading(true);
    const formData = new FormData();
    formData.append("id", detailMech.id);
    formData.append("verification_status", "rejected");
    axios
      .post(
        `${baseUrl}/superadmin/mechanics/updateVerificationStatus`,
        formData,
        {
          headers: { Authorization: `Bearer ${authToken}` },
        },
      )
      .then((response) => {
        if (response.data.success) {
          Swal.fire({
            icon: "success",
            title: "Success",
            text: "Mechanic rejected successfully!",
          });
          setDetailMech(null);
          viewAllMechanics();
        } else {
          Swal.fire({
            icon: "error",
            title: "Error",
            text: "Failed to reject mechanic.",
          });
        }
        setRejectLoading(false);
      })
      .catch(() => {
        Swal.fire({
          icon: "error",
          title: "Error",
          text: "Failed to reject mechanic.",
        });
        setRejectLoading(false);
      });
  };

  useEffect(() => {
    viewAllMechanics();
  }, []);

  useEffect(() => {
    const h = (e) => {
      if (
        !e.target.closest(".mech-menu-wrap") &&
        !e.target.closest(".mech-dropdown-portal")
      )
        setOpenMenu(null);
    };
    const close = () => setOpenMenu(null);
    document.addEventListener("mousedown", h);
    window.addEventListener("scroll", close, true);
    window.addEventListener("resize", close);
    return () => {
      document.removeEventListener("mousedown", h);
      window.removeEventListener("scroll", close, true);
      window.removeEventListener("resize", close);
    };
  }, []);

  useEffect(() => {
    setCnicTicked(false);
    setSpecTicked(false);
    setApproveLoading(false);
    setRejectLoading(false);
    setShowPdf(false);
  }, [detailMech]);

  const totalMechanics = mechanics.length;
  const pendingCount = mechanics.filter(
    (m) => m.verification_status === "pending",
  ).length;
  const activeCount = mechanics.filter(
    (m) => m.account_status === "active",
  ).length;
  const totalRevenue = mechanics.reduce((s, m) => s + (m.revenue || 0), 0);
  const verifiedList = mechanics.filter(
    (m) => m.verification_status === "verified",
  );
  const kycList = mechanics.filter((m) => m.verification_status === "pending");

  const pool =
    activeTab === "verified"
      ? verifiedList
      : activeTab === "kyc"
        ? kycList
        : mechanics;

  const totalPages = Math.ceil(pool.length / ITEMS_PER_PAGE) || 1;
  const startIdx = (currentPage - 1) * ITEMS_PER_PAGE;
  const pageRows = pool.slice(startIdx, startIdx + ITEMS_PER_PAGE);
  const pageNumbers = Array.from({ length: totalPages }, (_, i) => i + 1);

  const handleTabChange = (tab) => {
    setActiveTab(tab);
    setCurrentPage(1);
  };

  const handleBlock = (id) =>
    setMechanics((prev) =>
      prev.map((m) => (m.id === id ? { ...m, isBlocked: !m.isBlocked } : m)),
    );

  const handleDelete = (id) =>
    setMechanics((prev) => prev.filter((m) => m.id !== id));

  const isKyc = detailMech?.verification_status === "pending";

  // ✅ NEW — dynamic document URL, jaisa rental wale mein hai
  const pdfUrl = detailMech?.document
    ? `${baseUrlForFile}/${detailMech.document}`
    : null;

  // ── Ratings are only meaningful for verified (non-pending) mechanics ──
  const showRatingForMechanic = (m) => m.verification_status !== "pending";

  const infoFields = detailMech
    ? [
        { icon: <FiMail />, label: "Email", value: detailMech.email },
        { icon: <FiPhone />, label: "Phone", value: detailMech.phonenumber },
        { icon: <FiMapPin />, label: "Location", value: detailMech.location },
        {
          icon: <FiCalendar />,
          label: "Experience",
          value: detailMech.yearsofexperience,
        },
        {
          icon: <FiBriefcase />,
          label: "Specialization",
          value: detailMech.specialization,
        },
        { icon: <FiUsers />, label: "Mechanic ID", value: detailMech.id },
        {
          icon: <FiTool />,
          label: "Expertise in Machinery",
          value: detailMech.expertise,
        },
        ...(!isKyc
          ? [
              {
                icon: <FiCheckCircle />,
                label: "Total Jobs",
                value: `${detailMech.jobs} jobs`,
              },
              {
                icon: <FaRupeeSign  />,
                label: "Total Revenue",
                value: `Rs ${(detailMech.revenue || 0).toLocaleString()}`,
              },
              {
                icon: <FiStar />,
                label: "Average Rating",
                value:
                  detailMech.averageRating > 0
                    ? `⭐ ${detailMech.averageRating} (${detailMech.totalRatings || 0} ratings)`
                    : "No ratings yet",
              },
            ]
          : []),
      ]
    : [];

  const stats = [
    {
      icon: <FiTool size={28} />,
      value: totalMechanics,
      label: "Total Mechanics",
    },
    {
      icon: <FiAlertCircle size={28} />,
      value: pendingCount,
      label: "KYC Pending Requests",
    },
    {
      icon: <FaRupeeSign  size={28} />,
      value: `Rs ${totalRevenue.toLocaleString()}`,
      label: "Total Earnings",
    },
    {
      icon: <FiUsers size={28} />,
      value: activeCount,
      label: "Active Mechanics",
    },
  ];

  return (
    <AdminMasterLayout>
      <div className="mech-page">

        {/* ════════════ PAGE HEADING ════════════ */}
        <div className="mech-page-header">
          <h2 className="mech-page-title">Mechanic Management</h2>
          <p className="mech-page-subtitle">
            Manage registered mechanics, their verification status, and service ratings.
          </p>
        </div>

        {loading ? (
          <div className="mech-stats-grid">
            {[1, 2, 3, 4].map((i) => (
              <div className="mech-stat-card" key={i}>
                <div
                  className="mech-skeleton"
                  style={{ width: 54, height: 54, borderRadius: 13 }}
                />
                <div>
                  <div
                    className="mech-skeleton"
                    style={{ width: 70, height: 26, marginBottom: 6 }}
                  />
                  <div
                    className="mech-skeleton"
                    style={{ width: 110, height: 13 }}
                  />
                </div>
              </div>
            ))}
          </div>
        ) : (
          <>
            {/* STAT CARDS — icon → value → text, all in #FDB714 (Rental Owner List style) */}
            <div className="mech-stats-grid">
              {stats.map((s, i) => (
                <div className="mech-stat-card" key={i}>
                  <div className="mech-stat-icon">{s.icon}</div>
                  <div className="mech-stat-content">
                    <h3 className="mech-stat-val">{s.value}</h3>
                    <p className="mechanic-stat-lbl">{s.label}</p>
                  </div>
                </div>
              ))}
            </div>

            {/* TABS */}
            <div className="mech-tabs-wrapper">
              <div className="mech-tabs">
                <button
                  className={`mech-tab ${activeTab === "all" ? "active" : ""}`}
                  onClick={() => handleTabChange("all")}
                >
                  <FiTool size={14} /> All Mechanics
                  <span className="mech-tbadge">{mechanics.length}</span>
                </button>
                <button
                  className={`mech-tab ${activeTab === "verified" ? "active" : ""}`}
                  onClick={() => handleTabChange("verified")}
                >
                  <FiShield size={14} /> Verified Mechanics
                  {verifiedList.length > 0 && (
                    <span className="mech-tbadge ver">
                      {verifiedList.length}
                    </span>
                  )}
                </button>
                <button
                  className={`mech-tab ${activeTab === "kyc" ? "active" : ""}`}
                  onClick={() => handleTabChange("kyc")}
                >
                  <FiAlertCircle size={14} /> KYC Pending
                  {kycList.length > 0 && (
                    <span className="mech-tbadge kyc">{kycList.length}</span>
                  )}
                </button>
              </div>
            </div>

            {/* TABLE */}
            <div className="mech-table-wrapper">
              <table className="mech-table">
                <thead>
                  <tr>
                    <th className="col-name">Mechanic Name</th>
                    <th className="col-spec">Specialization</th>
                    {activeTab !== "kyc" && (
                      <th className="col-rating">Avg Rating</th>
                    )}
                    {activeTab !== "kyc" && (
                      <th className="col-revenue">Total Revenue</th>
                    )}
                    <th className="col-vstatus">Verification Status</th>
                    <th className="col-astatus">Account Status</th>
                    <th className="col-action">Actions</th>
                  </tr>
                </thead>
                <tbody>
                  {pageRows.length === 0 ? (
                    <tr>
                      <td colSpan={7}>
                        <div className="mech-empty">
                          <FiUsers size={40} />
                          <p>
                            {activeTab === "kyc"
                              ? "No KYC pending requests"
                              : activeTab === "verified"
                                ? "No verified mechanics yet"
                                : "No mechanics found"}
                          </p>
                        </div>
                      </td>
                    </tr>
                  ) : (
                    pageRows.map((m) => (
                      <tr
                        key={m.id}
                        className={m.isBlocked ? "blocked-row" : ""}
                      >
                        <td className="col-name">
                          <div className="mech-name-wrap">
                            <div className="mech-avatar">
                              {m?.image ? (
                                <img
                                  src={`${baseUrlForFile}/${m.image}`}
                                  alt={m.fullname}
                                  className="mech-avatar-img"
                                  onError={(e) =>
                                    console.log(
                                      "Image Load Error:",
                                      e.target.src,
                                    )
                                  }
                                />
                              ) : (
                                getInitials(m.fullname)
                              )}
                            </div>

                            <div className="mech-name-block">
                              <span className="mech-name">{m.fullname}</span>
                              <span className="mech-id">ID: {m.id}</span>
                            </div>
                          </div>
                        </td>
                        <td className="col-spec">
                          <div className="spec-name">{m.specialization}</div>
                          <div className="spec-loc">{m.location}</div>
                        </td>

                        {/* Avg Rating — only for verified mechanics */}
                        {activeTab !== "kyc" && (
                          <td className="col-rating">
                            {showRatingForMechanic(m) && m.averageRating > 0 ? (
                              <span className="rating-value">
                                ⭐ {m.averageRating}
                              </span>
                            ) : (
                              <span className="rating-na">—</span>
                            )}
                          </td>
                        )}

                        {activeTab !== "kyc" && (
                          <td className="col-revenue revenue-cell">
                            Rs {(m.revenue || 0).toLocaleString()}
                          </td>
                        )}
                        <td className="col-vstatus">
                          <span
                            className={`mech-badge ${m.verification_status}`}
                          >
                            {cap(m.verification_status)}
                          </span>
                        </td>
                        <td className="col-astatus">
                          <span className={`mech-badge ${m.account_status}`}>
                            {cap(m.account_status)}
                          </span>
                        </td>
                        <td className="col-action">
                          <div className="mech-menu-wrap">
                            <button
                              className="mech-menu-btn"
                              onClick={(e) => {
                                e.stopPropagation();
                                if (openMenu === m.id) {
                                  setOpenMenu(null);
                                } else {
                                  const rect =
                                    e.currentTarget.getBoundingClientRect();
                                  setMenuPos({
                                    top: rect.bottom + window.scrollY + 5,
                                    right: window.innerWidth - rect.right,
                                  });
                                  setOpenMenu(m.id);
                                }
                              }}
                              aria-label="Actions"
                            >
                              <FiMoreVertical size={17} />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )}
                </tbody>
              </table>
            </div>

            {/* PAGINATION */}
            {pool.length > 0 && (
              <div className="mech-pagi">
                <span className="mech-pagi-info">
                  Showing {Math.min(startIdx + 1, pool.length)}–
                  {Math.min(startIdx + ITEMS_PER_PAGE, pool.length)} of{" "}
                  {pool.length} results
                </span>
                <div className="mech-pagi-btns">
                  <button
                    className="mech-pg-btn"
                    onClick={() => setCurrentPage((p) => Math.max(1, p - 1))}
                    disabled={currentPage === 1}
                  >
                    <FiChevronLeft size={16} />
                  </button>
                  {pageNumbers.map((num) => (
                    <button
                      key={num}
                      className={`mech-pg-num ${currentPage === num ? "active-pg" : ""}`}
                      onClick={() => setCurrentPage(num)}
                    >
                      {num}
                    </button>
                  ))}
                  <button
                    className="mech-pg-btn"
                    onClick={() =>
                      setCurrentPage((p) => Math.min(totalPages, p + 1))
                    }
                    disabled={currentPage === totalPages}
                  >
                    <FiChevronRight size={16} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}

        {/* DETAIL / KYC MODAL */}
        {detailMech && (
          <div
            className="mech-modal-overlay"
            onClick={() => setDetailMech(null)}
          >
            <div
              className="mech-modal-box"
              onClick={(e) => e.stopPropagation()}
            >
              <div className="mech-modal-hdr">
                <div>
                  <h2 className="mech-modal-title">
                    {isKyc ? "KYC Details" : "Mechanic Details"}
                  </h2>
                  <p className="mech-modal-sub">
                    {isKyc
                      ? "Verification documents & information"
                      : "Mechanic profile information"}
                  </p>
                </div>
                <button
                  className="mech-modal-close"
                  onClick={() => setDetailMech(null)}
                >
                  <FiX size={18} />
                </button>
              </div>

              <div className="mech-profile-strip">
                <div className="mech-lrg-avatar">
                  {detailMech?.image ? (
                    <img
                      src={`${baseUrlForFile}/${detailMech.image}`}
                      alt={detailMech.fullname}
                      className="mech-avatar-large-img"
                    />
                  ) : (
                    getInitials(detailMech.fullname)
                  )}
                </div>
                <div>
                  <p className="mech-profile-name">{detailMech.fullname}</p>
                  <p className="mech-profile-sub">
                    {detailMech.specialization} &bull; {detailMech.location}
                  </p>
                  <span className={`mech-badge ${detailMech.status}`}>
                    {cap(detailMech.status)}
                  </span>
                </div>
              </div>

              <div className="mech-info-grid">
                {infoFields.map((f, i) => (
                  <div className="mech-info-item" key={i}>
                    <span className="mech-info-icon">{f.icon}</span>
                    <div>
                      <span className="mech-info-lbl">{f.label}</span>
                      <span className="mech-info-val">{f.value}</span>
                    </div>
                  </div>
                ))}
              </div>

              {isKyc && (
                <div className="mech-doc-section">
                  <h4 className="mech-doc-title">
                    <FiFileText size={15} /> Submitted Document
                  </h4>
                  <p className="mech-doc-hint">
                    Click the document card to preview
                  </p>
                  <button
                    className="mech-doc-card"
                    onClick={() => pdfUrl && setShowPdf(true)}
                    title={pdfUrl ? "Click to view document" : "No document uploaded"}
                  >
                    <div className="mech-doc-icon">
                      <FiFileText size={21} />
                    </div>
                    <div className="flex-grow-1">
                      <div className="mech-doc-name">Verification Document</div>
                      <div className="mech-doc-date">
                        Uploaded by {detailMech.fullname}
                      </div>
                    </div>
                    <div className="d-flex flex-column align-items-end gap-1">
                      <span className="mech-doc-status">
                        {pdfUrl ? "Submitted" : "Not Submitted"}
                      </span>
                      {pdfUrl && (
                        <span className="mech-doc-view">
                          <FiExternalLink size={12} /> View
                        </span>
                      )}
                    </div>
                  </button>

                  <div className="mech-verify-btns">
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <button
                        className={`mech-vbtn ${cnicTicked ? "ticked" : ""}`}
                        disabled={cnicDocLoading || cnicTicked}
                        onClick={() => {
                          setCnicDocLoading(true);
                          axios.post(`${baseUrl}/superadmin/kyc/verifyDocument`, {
                            id: detailMech.id,
                            actor_type: 'mechanic',
                            doc_type: 'cnic',
                          }, { headers: { Authorization: `Bearer ${authToken}` } })
                            .then((res) => {
                              setCnicDocLoading(false);
                              if (res.data.success) setCnicTicked(true);
                              else Swal.fire({ icon: 'error', title: 'Error', text: res.data.message });
                            })
                            .catch(() => setCnicDocLoading(false));
                        }}
                      >
                        <FiCheckCircle size={15} className="tick-icon" />{" "}
                        {cnicTicked ? "CNIC Verified" : cnicDocLoading ? "Verifying…" : "Verify CNIC"}
                      </button>
                      {!cnicTicked && (
                        <button
                          className="mech-vbtn"
                          style={{ fontSize: 11, color: '#dc3545', background: 'transparent', border: 'none', padding: 0 }}
                          disabled={cnicDocLoading}
                          onClick={() => {
                            Swal.fire({
                              title: 'Reject CNIC?',
                              input: 'text',
                              inputLabel: 'Reason (shown to the mechanic)',
                              showCancelButton: true,
                              confirmButtonText: 'Reject',
                              confirmButtonColor: '#dc3545',
                            }).then((result) => {
                              if (!result.isConfirmed) return;
                              setCnicDocLoading(true);
                              axios.post(`${baseUrl}/superadmin/kyc/rejectDocument`, {
                                id: detailMech.id,
                                actor_type: 'mechanic',
                                doc_type: 'cnic',
                                reason: result.value,
                              }, { headers: { Authorization: `Bearer ${authToken}` } })
                                .then((res) => {
                                  setCnicDocLoading(false);
                                  if (res.data.success) {
                                    setCnicTicked(false);
                                    Swal.fire({ icon: 'success', title: 'Rejected', text: 'CNIC rejected. The mechanic has been notified.' });
                                  }
                                })
                                .catch(() => setCnicDocLoading(false));
                            });
                          }}
                        >
                          <FiX size={12} /> Reject CNIC
                        </button>
                      )}
                    </div>
                    <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
                      <button
                        className={`mech-vbtn ${specTicked ? "ticked" : ""}`}
                        disabled={specDocLoading || specTicked}
                        onClick={() => {
                          setSpecDocLoading(true);
                          axios.post(`${baseUrl}/superadmin/kyc/verifyDocument`, {
                            id: detailMech.id,
                            actor_type: 'mechanic',
                            doc_type: 'license',
                          }, { headers: { Authorization: `Bearer ${authToken}` } })
                            .then((res) => {
                              setSpecDocLoading(false);
                              if (res.data.success) setSpecTicked(true);
                              else Swal.fire({ icon: 'error', title: 'Error', text: res.data.message });
                            })
                            .catch(() => setSpecDocLoading(false));
                        }}
                      >
                        <FiCheckCircle size={15} className="tick-icon" />{" "}
                        {specTicked ? "Specialization Verified" : specDocLoading ? "Verifying…" : "Verify Specialization"}
                      </button>
                      {!specTicked && (
                        <button
                          className="mech-vbtn"
                          style={{ fontSize: 11, color: '#dc3545', background: 'transparent', border: 'none', padding: 0 }}
                          disabled={specDocLoading}
                          onClick={() => {
                            Swal.fire({
                              title: 'Reject Specialization?',
                              input: 'text',
                              inputLabel: 'Reason (shown to the mechanic)',
                              showCancelButton: true,
                              confirmButtonText: 'Reject',
                              confirmButtonColor: '#dc3545',
                            }).then((result) => {
                              if (!result.isConfirmed) return;
                              setSpecDocLoading(true);
                              axios.post(`${baseUrl}/superadmin/kyc/rejectDocument`, {
                                id: detailMech.id,
                                actor_type: 'mechanic',
                                doc_type: 'license',
                                reason: result.value,
                              }, { headers: { Authorization: `Bearer ${authToken}` } })
                                .then((res) => {
                                  setSpecDocLoading(false);
                                  if (res.data.success) {
                                    setSpecTicked(false);
                                    Swal.fire({ icon: 'success', title: 'Rejected', text: 'Specialization rejected. The mechanic has been notified.' });
                                  }
                                })
                                .catch(() => setSpecDocLoading(false));
                            });
                          }}
                        >
                          <FiX size={12} /> Reject Specialization
                        </button>
                      )}
                    </div>
                  </div>

                  <div className="mech-notice">
                    <FiShield size={14} style={{ flexShrink: 0 }} />
                    <span>
                      Both CNIC and specialization must be verified before
                      approving the mechanic.
                    </span>
                  </div>
                </div>
              )}

              <div className="mech-modal-actions">
                {isKyc && (
                  <>
                    <button
                      className="mech-btn-approve"
                      onClick={acceptRequest}
                      disabled={approveLoading || rejectLoading || !cnicTicked || !specTicked}
                      title={(!cnicTicked || !specTicked) ? 'Verify both CNIC and Specialization first' : ''}
                    >
                      {approveLoading ? (
                        <>
                          <span className="mech-spinner" /> Verifying…
                        </>
                      ) : (
                        <>
                          <FiCheckCircle size={15} /> Verify Mechanic
                        </>
                      )}
                    </button>

                    <button
                      className="mech-btn-reject"
                      onClick={rejectRequest}
                      disabled={approveLoading || rejectLoading}
                    >
                      {rejectLoading ? (
                        <>
                          <span className="mech-spinner red" /> Rejecting…
                        </>
                      ) : (
                        <>
                          <FiX size={15} /> Reject Verification
                        </>
                      )}
                    </button>
                  </>
                )}
                <button
                  className="mech-btn-cancel"
                  style={!isKyc ? { marginLeft: 0 } : {}}
                  onClick={() => setDetailMech(null)}
                >
                  Close
                </button>
              </div>
            </div>
          </div>
        )}

        {/* PDF VIEWER MODAL */}
        {showPdf && pdfUrl && (
          <div className="mech-pdf-overlay" onClick={() => setShowPdf(false)}>
            <div className="mech-pdf-box" onClick={(e) => e.stopPropagation()}>
              <div className="mech-pdf-hdr">
                <div>
                  <h3 className="mech-pdf-title">Verification Document</h3>
                  <p className="mech-pdf-sub">Submitted Document</p>
                </div>
                <div className="d-flex align-items-center gap-2">
                  <a
                    href={pdfUrl}
                    target="_blank"
                    rel="noreferrer"
                    className="mech-pdf-open"
                  >
                    <FiExternalLink size={14} /> Open
                  </a>
                  <button
                    className="mech-modal-close"
                    onClick={() => setShowPdf(false)}
                  >
                    <FiX size={17} />
                  </button>
                </div>
              </div>
              <div className="mech-pdf-frame">
                <iframe
                  src={pdfUrl}
                  title="Verification Document"
                  width="100%"
                  height="100%"
                  style={{ border: "none" }}
                />
              </div>
            </div>
          </div>
        )}

        {/* DROPDOWN PORTAL */}
        {openMenu &&
          pageRows.find((m) => m.id === openMenu) &&
          createPortal(
            <div
              className="mech-dropdown-portal"
              style={{
                position: "fixed",
                top: menuPos.top,
                right: menuPos.right,
                minWidth: 165,
                background: "#FFFFFF",
                border: "1.5px solid #E8E8E8",
                borderRadius: 10,
                boxShadow: "0 8px 28px rgba(0,0,0,0.18)",
                zIndex: 999999,
                overflow: "hidden",
                animation: "ddSlide 0.16s ease",
              }}
            >
              {(() => {
                const m = pageRows.find((row) => row.id === openMenu);
                return (
                  <>
                    <button
                      className="mech-dd-item"
                      onClick={() => {
                        setDetailMech(m);
                        setCnicTicked(!!m.verifiedcnic);
                        setSpecTicked(!!m.verifiedlisence);
                        setOpenMenu(null);
                      }}
                    >
                      <FiEye size={14} />
                      {m.verification_status === "pending"
                        ? "View KYC Details"
                        : "View Details"}
                    </button>
                    <button
                      className="mech-dd-item"
                      onClick={() => {
                        handleToggleStatus(m.id);
                        setOpenMenu(null);
                      }}
                    >
                      {m.account_status === "active" ? (
                        <>
                          <FiLock size={14} /> Block
                        </>
                      ) : (
                        <>
                          <FiUnlock size={14} /> Unblock
                        </>
                      )}
                    </button>
                    <button
                      className="mech-dd-item danger"
                      onClick={() => {
                        handleDelete(m.id);
                        setOpenMenu(null);
                      }}
                    >
                      <FiTrash2 size={14} /> Delete
                    </button>
                  </>
                );
              })()}
            </div>,
            document.body,
          )}
      </div>
    </AdminMasterLayout>
  );
};

export default MechanicManagement;